import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuthStore } from '../../store/useAuthStore';

// PostMessage protocol types
interface LeelaAuthReadyMessage {
  type: 'LEELA_AUTH_READY';
  payload: {
    appType: 'client';
  };
}

interface LeelaAuthTokenMessage {
  type: 'LEELA_AUTH_TOKEN';
  payload: {
    customToken: string;
  };
}

interface LeelaAuthTokenRefreshMessage {
  type: 'LEELA_AUTH_TOKEN_REFRESH';
  payload: {
    customToken: string;
  };
}

interface LeelaAuthRevokeMessage {
  type: 'LEELA_AUTH_REVOKE';
  payload: {
    reason: string;
  };
}

interface LeelaAuthSuccessMessage {
  type: 'LEELA_AUTH_SUCCESS';
  payload: {
    uid: string;
  };
}

interface LeelaAuthErrorMessage {
  type: 'LEELA_AUTH_ERROR';
  payload: {
    error: string;
  };
}

interface LeelaClientLogoutMessage {
  type: 'LEELA_CLIENT_LOGOUT';
  payload: Record<string, never>;
}

interface LeelaOpenBlueprintMessage {
  type: 'LEELA_OPEN_BLUEPRINT';
  payload: Record<string, never>;
}

interface LeelaOpenExternalUrlMessage {
  type: 'LEELA_OPEN_EXTERNAL_URL';
  payload: {
    url: string;
  };
}

type IframeMessage =
  | LeelaAuthReadyMessage
  | LeelaAuthSuccessMessage
  | LeelaAuthErrorMessage
  | LeelaClientLogoutMessage
  | LeelaOpenBlueprintMessage
  | LeelaOpenExternalUrlMessage;

export interface PathAndCircleEmbedProps {
  className?: string;
  style?: React.CSSProperties;
}

// Support both production and local development
const isDevelopment =
  process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
const IFRAME_URL = isDevelopment
  ? 'http://localhost:5173'
  : 'https://path-and-circle-488217.web.app';
const ALLOWED_ORIGINS = [
  'https://path-and-circle-488217.web.app',
  'http://localhost:5173',
  'http://localhost:5174', // Alternative Vite port
];
const TOKEN_REFRESH_INTERVAL = 55 * 60 * 1000; // 55 minutes in milliseconds

/**
 * Embeds the authenticated Path & Circle client portal in an iframe.
 *
 * This component handles the postMessage-based authentication flow:
 * 1. Listens for LEELA_AUTH_READY from iframe
 * 2. Sends Firebase ID token via LEELA_AUTH_TOKEN
 * 3. Refreshes token every 55 minutes
 * 4. Sends LEELA_AUTH_REVOKE on logout
 *
 * Usage:
 * ```tsx
 * <PathAndCircleEmbed className="w-full h-screen" />
 * ```
 */
export const PathAndCircleEmbed: React.FC<PathAndCircleEmbedProps> = ({ className, style }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const user = useAuthStore((state) => state.user);
  const signOut = useAuthStore((state) => state.signOut);

  const [authSuccess, setAuthSuccess] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);

  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Loading messages to cycle through
  const loadingMessages = [
    'Optimizing your Blueprint',
    'Taking you to your Path & Circle journey',
    'Preparing your wellness portal',
    'Almost there...',
  ];

  // Get Firebase ID token directly from auth
  const getIdToken = useCallback(async (): Promise<string | null> => {
    try {
      if (!user) {
        throw new Error('Not authenticated');
      }

      const idToken = await user.getIdToken(true); // Force refresh
      console.log('[PathAndCircleEmbed] ID token retrieved');
      return idToken;
    } catch (error) {
      console.error('Error getting ID token:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setAuthError(errorMessage);
      return null;
    }
  }, [user]);

  // Send ID token to iframe (note: field is called "customToken" per protocol spec)
  const sendTokenToIframe = useCallback(
    async (isRefresh = false) => {
      const idToken = await getIdToken();

      if (!idToken || !iframeRef.current?.contentWindow) {
        console.error('Cannot send token: missing token or iframe');
        return;
      }

      const message: LeelaAuthTokenMessage | LeelaAuthTokenRefreshMessage = isRefresh
        ? {
            type: 'LEELA_AUTH_TOKEN_REFRESH',
            payload: { customToken: idToken },
          }
        : {
            type: 'LEELA_AUTH_TOKEN',
            payload: { customToken: idToken },
          };

      console.log(`[PathAndCircleEmbed] Sending ${message.type} to iframe`);
      iframeRef.current.contentWindow.postMessage(message, IFRAME_URL);
    },
    [getIdToken]
  );

  // Send revoke message to iframe
  const sendRevokeToIframe = useCallback((reason: string) => {
    if (!iframeRef.current?.contentWindow) {
      return;
    }

    const message: LeelaAuthRevokeMessage = {
      type: 'LEELA_AUTH_REVOKE',
      payload: { reason },
    };

    console.log('[PathAndCircleEmbed] Sending LEELA_AUTH_REVOKE to iframe');
    iframeRef.current.contentWindow.postMessage(message, IFRAME_URL);
  }, []);

  // Set up token refresh interval
  const setupTokenRefresh = useCallback(() => {
    // Clear any existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    // Set up new interval
    refreshIntervalRef.current = setInterval(() => {
      console.log('[PathAndCircleEmbed] Refreshing token...');
      sendTokenToIframe(true);
    }, TOKEN_REFRESH_INTERVAL);

    console.log('[PathAndCircleEmbed] Token refresh interval set up');
  }, [sendTokenToIframe]);

  // Clear token refresh interval
  const clearTokenRefresh = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
      console.log('[PathAndCircleEmbed] Token refresh interval cleared');
    }
  }, []);

  // Handle incoming postMessage events
  useEffect(() => {
    console.log('[PathAndCircleEmbed] Setting up message listener...');
    console.log('[PathAndCircleEmbed] Allowed origins:', ALLOWED_ORIGINS);
    console.log('[PathAndCircleEmbed] User authenticated:', !!user);

    const handleMessage = (event: MessageEvent<IframeMessage>) => {
      console.log('[PathAndCircleEmbed] Received message from:', event.origin);
      console.log('[PathAndCircleEmbed] Message data:', event.data);

      // Validate origin
      if (!ALLOWED_ORIGINS.includes(event.origin)) {
        console.warn('[PathAndCircleEmbed] Rejected message from untrusted origin:', event.origin);
        return;
      }

      const { data } = event;

      if (!data || typeof data !== 'object' || !data.type) {
        console.warn('[PathAndCircleEmbed] Invalid message format:', data);
        return;
      }

      console.log('[PathAndCircleEmbed] Received message:', data.type);

      switch (data.type) {
        case 'LEELA_AUTH_READY':
          console.log('[PathAndCircleEmbed] Iframe ready, sending token...');
          sendTokenToIframe(false);
          break;

        case 'LEELA_AUTH_SUCCESS':
          console.log('[PathAndCircleEmbed] Auth success:', data.payload.uid);
          setAuthSuccess(true);
          setIsLoading(false);
          setAuthError(null);
          setupTokenRefresh();
          break;

        case 'LEELA_AUTH_ERROR':
          console.error('[PathAndCircleEmbed] Auth error:', data.payload.error);
          setAuthError(data.payload.error);
          setIsLoading(false);
          clearTokenRefresh();
          break;

        case 'LEELA_CLIENT_LOGOUT':
          console.log('[PathAndCircleEmbed] Logout requested from iframe');
          signOut();
          break;

        case 'LEELA_OPEN_BLUEPRINT':
          console.log('[PathAndCircleEmbed] Open blueprint requested from iframe');
          // Dispatch custom event for ReportViewer or other components to listen to
          window.dispatchEvent(new CustomEvent('openBlueprint'));
          break;

        case 'LEELA_OPEN_EXTERNAL_URL':
          if (data.payload.url) {
            window.open(data.payload.url, '_blank');
          }
          break;

        default:
          console.log('[PathAndCircleEmbed] Unknown message type:', (data as any).type);
      }
    };

    window.addEventListener('message', handleMessage);

    // Set up timeout to detect if iframe never sends LEELA_AUTH_READY
    const readyTimeout = setTimeout(() => {
      if (isLoading && !authSuccess && !authError) {
        console.error('[PathAndCircleEmbed] Timeout: Iframe did not send LEELA_AUTH_READY message');
        setAuthError('Unable to connect to your wellness portal.');
        setIsLoading(false);
      }
    }, 15000); // 15 second timeout

    return () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(readyTimeout);
    };
  }, [
    sendTokenToIframe,
    setupTokenRefresh,
    clearTokenRefresh,
    signOut,
    user,
    isLoading,
    authSuccess,
    authError,
  ]);

  // Monitor auth state changes (logout detection)
  useEffect(() => {
    if (!user && authSuccess) {
      console.log('[PathAndCircleEmbed] User logged out, sending revoke...');
      sendRevokeToIframe('User logged out');
      clearTokenRefresh();
      setAuthSuccess(false);
      setIsLoading(true);
    }
  }, [user, authSuccess, sendRevokeToIframe, clearTokenRefresh]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTokenRefresh();
    };
  }, [clearTokenRefresh]);

  // Cycle through loading messages
  useEffect(() => {
    if (!isLoading) return;

    const interval = setInterval(() => {
      setLoadingMessageIndex((prev) => Math.min(prev + 1, loadingMessages.length - 1));
    }, 1000); // Change message every 1 seconds

    return () => clearInterval(interval);
  }, [isLoading, loadingMessages.length]);

  return (
    <div
      className={className}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        position: 'relative',
        ...style,
      }}
    >
      {/* Loading overlay */}
      {isLoading && !authError && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#ffffff',
            zIndex: 10,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <div
              style={{
                width: '48px',
                height: '48px',
                border: '5px solid #f0f0f0',
                borderTop: '5px solid #003442',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <div style={{ fontSize: '18px', color: '#1e1e1e', fontWeight: 600, marginTop: '8px' }}>
              Please wait
            </div>
            <div
              style={{
                fontSize: '15px',
                color: '#666',
                fontWeight: 400,
                minHeight: '21px',
                transition: 'opacity 0.3s ease-in-out',
              }}
            >
              {loadingMessages[loadingMessageIndex]}
            </div>
          </div>
        </div>
      )}

      {/* Error overlay */}
      {authError && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#ffffff',
            zIndex: 10,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '20px',
              maxWidth: '440px',
              padding: '40px',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: '56px' }}>😔</div>
            <div style={{ fontSize: '22px', fontWeight: 600, color: '#1e1e1e', lineHeight: '1.4' }}>
              We're having trouble loading your portal
            </div>
            <div style={{ fontSize: '15px', color: '#666', lineHeight: '1.6' }}>
              Please try refreshing the page. If the problem continues, reach out to your wellness
              coach or contact support.
            </div>
            <button
              onClick={() => window.location.reload()}
              style={{
                marginTop: '8px',
                padding: '12px 32px',
                backgroundColor: '#003442',
                color: '#ffffff',
                border: 'none',
                borderRadius: '24px',
                fontSize: '15px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#004e5e')}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#003442')}
            >
              Refresh Page
            </button>
          </div>
        </div>
      )}

      {/* Iframe */}
      <iframe
        ref={iframeRef}
        src={IFRAME_URL}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          flex: 1,
        }}
        title="Path & Circle Client Portal"
        sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
        onLoad={() => {
          console.log('[PathAndCircleEmbed] Iframe loaded');
          console.log(
            '[PathAndCircleEmbed] Iframe contentWindow:',
            !!iframeRef.current?.contentWindow
          );
        }}
        onError={(e) => {
          console.error('[PathAndCircleEmbed] Iframe error:', e);
          setAuthError('Failed to load iframe');
          setIsLoading(false);
        }}
      />

      {/* Spinner animation CSS */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};
